import { checklistPaymentAmount } from './selectors'
import { LinkedText } from '~/util/linked-text'
import { match } from '@st/pattern'
import type { ComponentProps, ReactNode } from 'react'
import type { STDocument, STChecklistItem, STMissingReason, ClientWorkflowPhase } from '@st/sdk'
import {
  EyeIcon,
  EyeCrossIcon,
  Button,
  Checklist,
  PaperClipIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon
} from '@st/theme'
import type { ChecklistMessage } from '~/features/checklists/module'
import type { DragHandlersProps, DragState } from '@st/react-util/use-draggable'

export function ChecklistListActions({
  phase,
  disabled,
  children,
  send
}: {
  send: (message: ChecklistMessage) => void
  phase: ClientWorkflowPhase
  disabled: boolean
  children?: ReactNode
}) {
  return (
    <div className="flex items-center justify-end gap-2">
      {children}
      <Button
        disabled={disabled}
        leadingIcon={<PlusIcon className="size-4" />}
        onClick={() => send({ type: 'openChecklistDialog', request: { mode: 'create', phase } })}
      >
        Add item
      </Button>
    </div>
  )
}

export function ChecklistEmptyState({
  children,
  instructions
}: {
  children?: ReactNode
  instructions: string
}) {
  return (
    <div className="rounded-md border border-gray-200 px-6 py-8 text-center">
      <p className="text-sm font-medium text-gray-700">No checklist items yet</p>
      <p className="mt-1 text-sm text-gray-500">{instructions}</p>
      {children && <div className="mt-4 flex justify-center">{children}</div>}
    </div>
  )
}

export function ChecklistDocumentLinks({
  documents,
  onSelect
}: {
  documents: STDocument[]
  onSelect: (document: STDocument) => void
}) {
  return (
    <span className="space-x-3">
      {documents.map((document) => (
        <button
          key={document.id}
          className="inline-flex min-w-0 max-w-full items-baseline gap-1 align-baseline text-sm text-blue-500 hover:underline"
          onClick={() => onSelect(document)}
        >
          <PaperClipIcon className="size-3 shrink-0 self-center" />
          <span className="truncate">{document.name}</span>
        </button>
      ))}
    </span>
  )
}

export function ChecklistItemActions({
  name,
  disabled,
  onAction
}: {
  name: string
  disabled: boolean
  onAction: (action: 'attachments' | 'edit' | 'delete') => void
}) {
  return (
    <div className="flex items-center gap-2 opacity-0 group-focus-within/checklist-row:opacity-100 group-hover/checklist-row:opacity-100 [@media(hover:none)]:opacity-100">
      <Checklist.Button
        disabled={disabled}
        tip="Manage attachments"
        ariaLabel={`Attachments for ${name}`}
        onClick={() => onAction('attachments')}
      >
        <PaperClipIcon className="size-4" />
      </Checklist.Button>
      <Checklist.Button
        disabled={disabled}
        tip="Edit checklist item"
        ariaLabel={`Edit ${name}`}
        onClick={() => onAction('edit')}
      >
        <PencilSimpleIcon className="size-4" />
      </Checklist.Button>
      <Checklist.Button
        disabled={disabled}
        tip="Delete checklist item"
        ariaLabel={`Delete ${name}`}
        onClick={() => onAction('delete')}
      >
        <TrashIcon className="size-4" />
      </Checklist.Button>
    </div>
  )
}

export function ChecklistItemName({ item }: { item: Pick<STChecklistItem, 'name' | 'dueOn'> }) {
  return (
    <div>
      {item.name}
      {item.dueOn && (
        <span className="mt-1 block w-fit rounded bg-gray-100 px-2 py-0.5 text-xs font-normal text-gray-500">
          Due {item.dueOn}
        </span>
      )}
    </div>
  )
}

export function ChecklistVisibilityButton({
  item,
  disabled,
  send
}: {
  item: STChecklistItem
  disabled: boolean
  send: (message: ChecklistMessage) => void
}) {
  return (
    <div
      className={match(item.visibility)
        .with('organization', () => 'visible')
        .otherwise(
          () =>
            'opacity-0 group-focus-within/checklist-row:opacity-100 group-hover/checklist-row:opacity-100 [@media(hover:none)]:opacity-100'
        )}
    >
      <Checklist.Button
        disabled={disabled}
        tip={match(item.visibility)
          .with('organization', () => 'Unhide from client')
          .otherwise(() => 'Hide from client')}
        ariaLabel={match(item.visibility)
          .with('organization', () => 'Unhide from client')
          .otherwise(() => 'Hide from client')}
        onClick={() => send({ type: 'visibilityToggled', item })}
      >
        {match(item.visibility)
          .with('organization', () => <EyeCrossIcon className="size-4" />)
          .otherwise(() => (
            <EyeIcon className="size-4" />
          ))}
      </Checklist.Button>
    </div>
  )
}

export function checklistDragProps({
  item,
  isArchived,
  dragHandlers,
  dragState
}: {
  item: STChecklistItem
  isArchived: boolean
  dragHandlers: (item: STChecklistItem) => DragHandlersProps
  dragState: DragState<STChecklistItem>
}) {
  const dragStatus: ComponentProps<typeof Checklist.Row>['dragStatus'] = (() => {
    if (dragState.status == 'hovering' && dragState.target == item) {
      return dragState.relation
    } else if (
      (dragState.status == 'hovering' || dragState.status == 'moving') &&
      dragState.item == item
    ) {
      return 'dragging'
    }
    return undefined
  })()

  return {
    draggable: !isArchived,
    dragStatus,
    ...match(isArchived)
      .with(true, () => ({}))
      .with(false, () => dragHandlers(item))
      .exhaustive()
  }
}

export function ChecklistItemRow({
  item,
  documents = [],
  incompleteReason,
  onSelectDocument,
  checkbox,
  actions,
  ...rowProps
}: {
  item: Pick<STChecklistItem, 'name' | 'dueOn' | 'note' | 'context'> &
    Partial<Pick<STChecklistItem, 'status' | 'cancelled'>>
  documents?: STDocument[]
  incompleteReason?: STMissingReason
  onSelectDocument?: (documentId: string) => void
  checkbox?: ReactNode
  actions?: ReactNode
} & Omit<ComponentProps<typeof Checklist.Row>, 'children'>) {
  const amountLabel = checklistPaymentAmount(item)

  return (
    <Checklist.Row
      checked={item.status === 'complete'}
      ariaLabel={`Checklist item: ${item.name}`}
      {...rowProps}
    >
      <Checklist.DragHandleCell enabled={rowProps.draggable === true} />
      {checkbox ?? (
        <Checklist.CheckboxCell checked={item.status === 'complete'} disabled onChange={() => {}} />
      )}
      <Checklist.MainCell crossedOut={item.cancelled === true}>
        <ChecklistItemName item={item} />
      </Checklist.MainCell>
      <div className="flex min-w-0 items-center gap-4 py-2 pr-3 text-sm text-gray-700">
        <div className="min-w-0 flex-1">
          <span className="whitespace-pre-wrap">
            {item.note && <LinkedText text={item.note} />}
          </span>
          {documents.length > 0 && (
            <>
              {item.note && <span className="text-gray-500"> · </span>}
              <ChecklistDocumentLinks
                documents={documents}
                onSelect={(document) => onSelectDocument?.(document.id)}
              />
            </>
          )}
          {documents.length === 0 && incompleteReason && (
            <>
              {item.note && ' '}
              <span className="inline-block rounded-sm bg-blue-200 px-1 text-sm text-blue-700">
                {incompleteReason.name}
              </span>
            </>
          )}
        </div>
        {amountLabel && (
          <p className="shrink-0 text-right text-base font-semibold tabular-nums text-gray-900">
            {amountLabel}
          </p>
        )}
      </div>
      <Checklist.ActionsCell>{actions}</Checklist.ActionsCell>
    </Checklist.Row>
  )
}
